/**
 * Main JavaScript Entry Point
 *
 * This file imports the main SCSS and handles any JavaScript functionality.
 * Vite will automatically process HMR (Hot Module Replacement) in development.
 *
 * @package Wohngut
 * @since 1.0.0
 */

// Import main styles
import './scss/style.scss';

// Import utilities (available for all features)
import * as DOM from './js/utilities/dom-helpers.js';

// Import Elementor Accordion (auto-initializes)
import './js/features/elementor-accordion.js';

// Make utilities available globally for debugging (development only)
if (import.meta.env.DEV) {
    window.WohngutDOM = DOM;
}

/**
 * Report critical component error
 * Logs error and provides visual feedback for critical failures
 *
 * @param {string} componentName - Name of the component that failed
 * @param {Error} error - Error object
 * @param {HTMLElement|null} element - Optional element to mark as failed
 */
function reportCriticalError(componentName, error, element = null) {
    console.error(`❌ Critical Error in ${componentName}:`, error);

    // Mark element as failed (visual feedback)
    if (element) {
        element.style.opacity = '0.5';
        element.style.pointerEvents = 'none';
        element.setAttribute('aria-disabled', 'true');
        element.setAttribute('title', `${componentName} konnte nicht geladen werden`);
    }

    // Show dev notice in development mode
    if (import.meta.env.DEV) {
        console.warn(`💡 Dev hint: Check if ${componentName} module exports correctly`);
    }
}

// HMR for CSS changes (development only)
if (import.meta.hot) {
    import.meta.hot.accept('./scss/style.scss', () => {
        console.log('🔥 SCSS updated via HMR');
    });
}

/**
 * Initialize theme features on DOM Ready
 */
document.addEventListener('DOMContentLoaded', () => {
    console.log('✅ Wohngut Theme loaded');

    // Initialize Navigation Overlay if present (critical feature)
    const navOverlay = document.querySelector('[data-nav-overlay]');
    if (navOverlay) {
        const burger = document.querySelector('[data-nav-burger]');
        import('./js/features/navigation-overlay.js')
            .then(module => {
                new module.default();
            })
            .catch(err => {
                reportCriticalError('NavigationOverlay', err, burger);
            });
    }

    // Initialize Standorte Dropdown if present
    if (document.querySelector('[data-standorte-dropdown]')) {
        import('./js/features/standorte-dropdown.js')
            .then(module => {
                new module.default();
            })
            .catch(err => {
                console.error('Failed to load StandorteDropdown:', err);
            });
    }

    // Initialize Sticky Header if header present
    if (document.querySelector('[data-nav-header]')) {
        import('./js/features/sticky-header.js')
            .then(module => {
                new module.default();
            })
            .catch(err => {
                console.error('Failed to load StickyHeader:', err);
            });
    }

    // Initialize WG Gallery Slider if present
    if (document.querySelector('[data-gallery-slider]')) {
        import('./js/features/wg-gallery-slider.js')
            .then(module => {
                new module.default();
            })
            .catch(err => {
                console.error('Failed to load WGGallerySlider:', err);
            });
    }

    if (document.querySelector('[data-news-slider]')) {
        initNewsSlider();
    }

    // Initialize Contact Modal if present
    if (document.querySelector('[data-contact-modal]')) {
        import('./js/features/contact-modal.js')
            .then(module => {
                new module.default();
            })
            .catch(err => {
                console.error('Failed to load ContactModal:', err);
            });
    }

    // Initialize Bewerbung Modal if present
    if (document.querySelector('[data-bewerbung-modal]')) {
        import('./js/features/bewerbung-modal.js')
            .then(module => {
                new module.default();
            })
            .catch(err => {
                console.error('Failed to load BewerbungModal:', err);
            });
    }
});

/**
 * Initialize Remote Arrows Controller
 *
 * Centralized function to prevent multiple initializations
 */
let remoteArrowsInitialized = false;
function initRemoteArrows() {
    if (remoteArrowsInitialized) {
        console.log('⚠️ RemoteArrows already initialized, skipping');
        return;
    }

    if (document.querySelector('[data-arrow-direction]')) {
        remoteArrowsInitialized = true;
        import('./js/features/remote-arrows-controller.js')
            .then(module => {
                new module.default();
                console.log('✅ RemoteArrows initialized');
            })
            .catch(err => {
                console.error('Failed to load RemoteArrows:', err);
                remoteArrowsInitialized = false; // Reset on error
            });
    }
}

let newsSliderManager = null;
function initNewsSlider(context = document) {
    if (newsSliderManager) {
        newsSliderManager.init?.(context);
        return;
    }

    import('./js/features/news-slider.js')
        .then(module => {
            const Manager = module.default;
            newsSliderManager = new Manager();
        })
        .catch(err => {
            console.error('Failed to load NewsSlider:', err);
        });
}

/**
 * Elementor Frontend Hook
 *
 * Try to initialize after Elementor loads widgets
 * This ensures Swiper instances are available
 */
if (typeof jQuery !== 'undefined') {
    // Listen to Elementor's init event
    jQuery(window).on('elementor/frontend/init', function() {
        console.log('🎨 Elementor frontend initialized');

        // Delay to ensure Swiper and all widgets are ready
        setTimeout(function() {
            initRemoteArrows();
            initNewsSlider();
        }, 500);
    });
}

/**
 * Window Load Fallback
 *
 * If Elementor hook doesn't fire or arrows added later,
 * try initialization on window.load as final fallback
 */
window.addEventListener('load', () => {
    // Additional delay for Elementor async widget loading
    setTimeout(() => {
        initRemoteArrows();
    }, 1000);
});
